<template>
	<v-menu
		v-if="chatRoom?.included_project_lines?.length > 0"
		offset-y
		bottom
		left
		:close-on-content-click="true"
		min-width="240"
		max-width="260"
	>
		<template v-slot:activator="{ on, attrs }">
			<v-tooltip bottom>
				<template v-slot:activator="{ on: onTooltip, attrs: attrsTooltip }">
					<button
						class="pl-filter-btn"
						:class="{ 'is-active': project_line_id !== null }"
						v-bind="{ ...attrs, ...attrsTooltip }"
						v-on="{ ...on, ...onTooltip }"
					>
						<v-icon size="11" color="white" style="margin-right: 2px;">mdi-filter-variant</v-icon>
						<span class="pl-filter-label">
							{{ activeLabel }}
						</span>
						<span v-if="totalUnseen > 0" class="pl-filter-badge">
							{{ totalUnseen > 99 ? '+99' : totalUnseen }}
						</span>
					</button>
				</template>
				<span>Filtrar por línea de proyecto</span>
			</v-tooltip>
		</template>

		<div class="pl-dropdown">
			<!-- Header -->
			<div class="pl-dd-header">
				<p class="pl-dd-header-label">Filtrar por línea de proyecto</p>
			</div>

			<!-- Todos -->
			<div
				class="pl-dd-item"
				:class="{ 'pl-dd-item--selected': project_line_id === null }"
				@click="$emit('update:project_line_id', null)"
			>
				<span class="pl-tag pl-tag--all">OTROS</span>
				<div class="pl-dd-item-info">
					<p class="pl-dd-item-name">Otros mensajes</p>
				</div>
				<span
					v-if="getUnseenMessageCountForOthers() > 0"
					class="pl-unseen-badge"
				>
					{{ getUnseenMessageCountForOthers() > 99 ? '+99' : getUnseenMessageCountForOthers() }}
				</span>
			</div>

			<!-- Líneas de proyecto incluidas -->
			<template v-for="pl in chatRoom.included_project_lines">
				<div
					:key="pl.id"
					class="pl-dd-item"
					:class="{ 'pl-dd-item--selected': project_line_id === pl.id }"
					@click="$emit('update:project_line_id', pl.id)"
				>
					<span class="pl-tag">LP{{ pl.number }}</span>
					<div class="pl-dd-item-info">
						<p class="pl-dd-item-name">{{ getProjectLineName(pl) }}</p>
						<p class="pl-dd-item-sub">{{ getProjectLineAddress(pl) }}</p>
					</div>
					<span
						v-if="getUnseenMessageCountForProjectLine(pl.id) > 0"
						class="pl-unseen-badge"
					>
						{{ getUnseenMessageCountForProjectLine(pl.id) > 99 ? '+99' : getUnseenMessageCountForProjectLine(pl.id) }}
					</span>
				</div>
			</template>

			<!-- Líneas sin chat -->
			<template v-if="userProjects && userProjects.length > 0">
				<div class="pl-dd-section-label">Sin chat</div>
				<div
					v-for="(pl, index) in userProjects"
					:key="'user_pl-' + index"
					class="pl-dd-item pl-dd-item--no-chat"
					@click="$emit('select-user-project', pl)"
				>
					<span class="pl-tag pl-tag--no-chat">LP{{ pl.number }}</span>
					<div class="pl-dd-item-info">
						<p class="pl-dd-item-name">{{ getProjectLineName(pl) }}</p>
						<p class="pl-dd-item-sub pl-dd-item-sub--warn">Sin chat activo</p>
					</div>
				</div>
			</template>
		</div>
	</v-menu>
</template>

<script>
export default {
	name: 'ProjectLineFilterMenu',
	props: {
		chatRoom: {
			type: Object,
			required: true
		},
		project_line_id: {
			type: Number,
			default: null
		},
		userProjects: {
			type: Array,
			default: () => []
		},
		conversationMessages: {
			type: Array,
			default: () => []
		}
	},
	computed: {
		activeLabel() {
			if (!this.project_line_id) return 'Filtro'
			const pl = this.chatRoom.included_project_lines?.find(p => p.id === this.project_line_id)
			return pl ? `LP${pl.number}` : 'Filtro'
		},
		totalUnseen() {
			const projectLinesUnseen = this.chatRoom.included_project_lines?.reduce((sum, pl) => {
				return sum + this.getUnseenMessageCountForProjectLine(pl.id)
			}, 0) || 0
			const othersUnseen = this.getUnseenMessageCountForOthers()
			return projectLinesUnseen + othersUnseen
		}
	},
	methods: {
		getProjectLineName(pl) {
			const assoc = pl.associations?.find(a => a.morphable?.name || a.morphable?.info?.project_detail)
			return assoc?.morphable?.name || assoc?.morphable?.info?.project_detail || '--'
		},
		getProjectLineAddress(pl) {
			const assoc = pl.associations?.find(a => a.morphable?.info?.address?.address)
			return assoc?.morphable?.info?.address?.address || null
		},
		getUnseenMessageCountForProjectLine(projectLineId) {
			return this.conversationMessages.filter(msg =>
				parseInt(msg?.relatable?.relatable_id) === parseInt(projectLineId) &&
				(!msg.seen_message || msg.seen_message.length === 0)
			).length
		},
		getUnseenMessageCountForOthers() {
			return this.conversationMessages.filter(msg => {
				const relatable_id = msg?.relatable?.relatable_id
				const isProjectLine = this.chatRoom.included_project_lines?.some(pl => pl.id === parseInt(relatable_id))
				return !isProjectLine && (!msg.seen_message || msg.seen_message.length === 0)
			}).length
		}
	}
}
</script>

<style scoped>
/* ── Botón activador ── */
.pl-filter-btn {
	display: inline-flex;
	align-items: center;
	gap: 3px;
	padding: 2px 7px;
	border-radius: 4px;
	border: 1px solid rgba(255, 255, 255, 0.3);
	background: rgba(255, 255, 255, 0.1);
	color: white;
	font-size: 10px;
	font-weight: 500;
	cursor: pointer;
	transition: background 0.12s, border-color 0.12s;
	white-space: nowrap;
	font-family: inherit;
	line-height: 1;
	height: 22px;
}
.pl-filter-btn:hover {
	background: rgba(255, 255, 255, 0.2);
	border-color: rgba(255, 255, 255, 0.5);
}
.pl-filter-btn.is-active {
	background: rgba(255, 255, 255, 0.22);
	border-color: rgba(255, 255, 255, 0.6);
}
.pl-filter-label {
	color: white;
	font-size: 10px;
}
.pl-filter-badge {
	background: #FF120B;
	color: white;
	font-size: 9px;
	border-radius: 8px;
	padding: 0 4px;
	min-width: 14px;
	text-align: center;
	line-height: 14px;
}

/* ── Dropdown ── */
.pl-dropdown {
	background: white;
	border-radius: 8px;
	overflow: hidden;
	max-height: 400px;
	overflow-y: auto;
}
.pl-dd-header {
	padding: 7px 12px 5px;
	border-bottom: 0.5px solid #e8e8e8;
}
.pl-dd-header-label {
	font-size: 10px;
	font-weight: 600;
	color: #aaa;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	margin: 0;
}
.pl-dd-section-label {
	font-size: 10px;
	font-weight: 600;
	color: #bbb;
	padding: 5px 12px 3px;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	background: #f8f8f8;
}

/* ── Items ── */
.pl-dd-item {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 7px 12px;
	cursor: pointer;
	border-bottom: 0.5px solid #f0f0f0;
	transition: background 0.1s;
}
.pl-dd-item:last-child {
	border-bottom: none;
}
.pl-dd-item:hover {
	background: #f5f5f5;
}
.pl-dd-item--selected {
	background: #FFF0EF !important;
}
.pl-dd-item--no-chat {
	opacity: 0.85;
}
.pl-dd-item-info {
	flex: 1;
	min-width: 0;
}
.pl-dd-item-name {
	font-size: 11px;
	font-weight: 500;
	color: #222;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	margin: 0;
}
.pl-dd-item-sub {
	font-size: 10px;
	color: #aaa;
	margin: 0;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
.pl-dd-item-sub--warn {
	color: #FF8C00;
}

/* ── Tags y badges ── */
.pl-tag {
	font-size: 9px;
	font-weight: 700;
	background: #524D4D;
	color: white;
	border-radius: 3px;
	padding: 2px 5px;
	flex-shrink: 0;
	min-width: 30px;
	text-align: center;
	letter-spacing: 0.02em;
}
.pl-tag--all {
	background: #888;
}
.pl-tag--no-chat {
	background: #ccc;
	color: #555;
}
.pl-dd-item--selected .pl-tag:not(.pl-tag--all):not(.pl-tag--no-chat) {
	background: #FF120B;
}
.pl-unseen-badge {
	width: 18px;
	height: 18px;
	background: #FF120B;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 9px;
	color: white;
	font-weight: 600;
	flex-shrink: 0;
}
</style>